/**
 * Pure session-lifecycle → PostHog telemetry helper (gh #876).
 *
 * Emits a single `webapp.session_end` event at every point a webapp session
 * terminates, so the operator can measure how often users are logged out
 * mid-use and why (gh #876 complaint 1: "no way to currently measure how
 * often this happens or why"). Nothing else in the app emits session
 * lifecycle events today — terminations only hit `logger.warn` (console).
 *
 * --------------------------------------------------------------------
 *  Why a separate module (mirrors lib/telemetry/web-vitals.ts)
 * --------------------------------------------------------------------
 *
 * The provenance feature locks its own 4-event PostHog allow-list inside
 * `components/evidence/shared/provenance-telemetry.ts`. Session lifecycle is a
 * globally-scoped concern (every route, every user), so it lives in
 * `lib/telemetry/**` alongside `web-vitals.ts` — the webapp's
 * `eslint.config.mjs::no-restricted-imports` ban on `posthog-js` inside
 * `components/evidence/**` deliberately does NOT cover `lib/telemetry/**`.
 *
 * --------------------------------------------------------------------
 *  CJIS payload allow-list (PE-10 / no-CJI-in-payloads)
 * --------------------------------------------------------------------
 *
 * `buildSessionEndPayload` DESTRUCTURES an explicit safe-field allow-list —
 * `{reason, route, sessionAgeS, wasIdle, hadInflightRequest}` — before
 * forwarding to PostHog. This is the same structural discipline used by
 * `web-vitals.ts::buildWebVitalPayload`, and the same reason: a future
 * regression that spreads a richer context object into the event cannot leak
 * a case id, defendant name, audio filename, or query string.
 *
 * `route` is intentionally `window.location.pathname` ONLY — never
 * `.search` / `.hash` — because query strings in this app can carry a
 * `?redirect=/cases/<uuid>/...` value (CJI-adjacent). The pathname itself is
 * a route template class (`/cases/[caseId]/recordings`) whose dynamic
 * segment is an opaque tenant-scoped UUID, not CJI. The
 * `__tests__/lib/telemetry/session-telemetry.test.ts` "payload allow-list"
 * suite locks the exact key set so any regression fires a test failure.
 *
 * --------------------------------------------------------------------
 *  Wiring (registration pattern, keeps this module posthog-free)
 * --------------------------------------------------------------------
 *
 * Unlike `web-vitals.ts` (called from a single `useReportWebVitals`
 * callback), session terminations fire from several modules —
 * `lib/auth-reset.ts` (every AuthResetReason), `app/providers.tsx` (idle
 * lock, 12h cap, unexpected authenticated→null redirect). Threading a bound
 * `posthog.capture` through all of them (some deep in `lib/api-client.ts`'s
 * import graph) would be awkward, so `app/providers.tsx` REGISTERS the
 * capture fn + an `isIdle` getter once via `registerSessionTelemetry`, and
 * call sites use the fire-and-forget `emitSessionEnd(reason)`. This mirrors
 * `lib/auth-reset.ts::registerAuthResetHandlers`. The module never imports
 * `posthog-js` directly, so it stays fully unit-testable and adds nothing to
 * `lib/api-client.ts`'s bundle graph.
 *
 * If a termination fires before `Providers` has registered (unlikely — it
 * mounts at the root), the PostHog forward is a no-op but the dev
 * `console.debug` sink still fires so the operator can verify wiring during
 * `npm run dev` (where `providers.tsx` opts the PostHog singleton out of
 * capturing anyway — same dev-signal rationale as `web-vitals.ts`).
 */

export const SESSION_END_EVENT_NAME = 'webapp.session_end';

/**
 * Every distinct way a webapp session ends. Superset of
 * `lib/auth-reset.ts::AuthResetReason` (all reset reasons are session ends)
 * plus the non-reset terminations `providers.tsx` owns directly:
 *   - `idle-lock`       CJIS 5.6.3.1 / AC-11 30-min idle lock (lockSession).
 *   - `session-cap`     CJIS IA-11 12h absolute cap hit during the 50-min
 *                       background refresh interval.
 *   - `session-expired` authenticated→null observed by the client with no
 *                       in-flight fetch to trip api-client's 401 reset (12h
 *                       cap reached during a silent Firebase token refresh,
 *                       lost MFA claim, or token-read failure). The gh #876
 *                       complaint-2 redirect path.
 */
export const SESSION_END_REASONS = [
  'idle-lock',
  'session-cap',
  'session-expired',
  'profile-401',
  'api-401-refresh-failed',
  'login-session-sync-failed',
  'manual-user-reset',
  'no-app-access',
] as const;

export type SessionEndReason = (typeof SESSION_END_REASONS)[number];

/**
 * Capture function shape — matches `posthog-js`'s `capture` method. Typed
 * structurally so the test suite can pass a `vi.fn()` without importing
 * `posthog-js`, and so `app/providers.tsx` can register
 * `posthog.capture.bind(posthog)`.
 */
export type PosthogCapture = (
  event: string,
  properties: Record<string, unknown>
) => void;

/**
 * Reasons whose trigger is a failing authenticated API call — i.e. an API
 * request was in flight when the session ended. Used as the DEFAULT for
 * `had_inflight_request` when the caller does not pass an explicit value.
 *
 * Deriving the flag from the reason (rather than a live global request
 * counter) is deliberate: `lib/api-client.ts` issues `fetch` per-method with
 * no single start/finish choke point to instrument symmetrically, so a
 * counter would be fragile and leak. `profile-401`, `api-401-refresh-failed`
 * and `no-app-access` are, by construction, raised from inside a failing
 * request handler — the in-flight signal is exact for them. Idle lock, the
 * 12h cap, and the unexpected `session-expired` transition are not tied to a
 * failing fetch, so they default to `false` (callers may still override).
 */
const INFLIGHT_REASONS: ReadonlySet<SessionEndReason> = new Set([
  'profile-401',
  'api-401-refresh-failed',
  'no-app-access',
]);

export interface SessionEndContext {
  /** Route template class; pathname only (never search/hash). Defaults to `window.location.pathname`. */
  route?: string | null;
  /** Seconds since the session was established. Defaults to `Date.now() - markSessionStart()`. */
  sessionAgeS?: number | null;
  /** Was the CJIS idle timer idle at termination. Defaults to the registered `isIdle` getter. */
  wasIdle?: boolean;
  /** Was an authenticated API request in flight. Defaults per `INFLIGHT_REASONS`. */
  hadInflightRequest?: boolean;
}

export interface SessionEndPayloadInput extends SessionEndContext {
  reason: SessionEndReason;
}

/**
 * Build the allow-listed payload. Pure; exported so the dev `console.debug`
 * sink and the PostHog forward share one destructure (single source of truth
 * for what we send anywhere). ANY field not in the destructure is dropped.
 */
export function buildSessionEndPayload(
  input: SessionEndPayloadInput
): Record<string, unknown> {
  // Structural allow-list — see CJIS-payload-allow-list block in the header.
  const { reason, route, sessionAgeS, wasIdle, hadInflightRequest } = input;
  return {
    reason,
    route: route ?? null,
    session_age_s:
      typeof sessionAgeS === 'number' && Number.isFinite(sessionAgeS)
        ? sessionAgeS
        : null,
    was_idle: Boolean(wasIdle),
    had_inflight_request: Boolean(hadInflightRequest),
  };
}

/**
 * Emit a single session-end event to PostHog (and to `console.debug` in
 * development). Parameterized on `posthogCapture` so tests inject a spy;
 * production goes through `emitSessionEnd`, which resolves the registered
 * capture fn.
 */
export function reportSessionEnd(
  input: SessionEndPayloadInput,
  posthogCapture: PosthogCapture | null | undefined
): void {
  const payload = buildSessionEndPayload(input);

  // Dev sink — fires regardless of posthogCapture. providers.tsx opts posthog
  // out of capturing in NODE_ENV === 'development', so without this the
  // operator has no signal during `npm run dev`.
  if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
    console.debug(`[session-telemetry] ${SESSION_END_EVENT_NAME}`, payload);
  }

  if (!posthogCapture) return;
  try {
    posthogCapture(SESSION_END_EVENT_NAME, payload);
  } catch {
    // Telemetry must never break auth teardown. Swallow.
  }
}

// ---------------------------------------------------------------------------
// Registered runtime context (set once by app/providers.tsx)
// ---------------------------------------------------------------------------

let registeredCapture: PosthogCapture | null = null;
let isIdleGetter: (() => boolean) | null = null;
let sessionStartMs: number | null = null;

export interface SessionTelemetryRegistration {
  capture?: PosthogCapture | null;
  isIdle?: (() => boolean) | null;
}

/** Register the PostHog capture fn + idle getter. Called once by Providers. */
export function registerSessionTelemetry(reg: SessionTelemetryRegistration): void {
  if ('capture' in reg) registeredCapture = reg.capture ?? null;
  if ('isIdle' in reg) isIdleGetter = reg.isIdle ?? null;
}

/** Record the moment a session was (re)established, for session_age_s. */
export function markSessionStart(ts: number = Date.now()): void {
  sessionStartMs = ts;
}

/** Forget the session start (after a session ends, or on explicit reset). */
export function clearSessionStart(): void {
  sessionStartMs = null;
}

function safeBool(fn: (() => boolean) | null): boolean {
  if (!fn) return false;
  try {
    return Boolean(fn());
  } catch {
    return false;
  }
}

/**
 * Fire-and-forget session-end emit. Resolves route / age / idle /
 * inflight defaults from registered context, then forwards through
 * `reportSessionEnd`. One-shot: clears the recorded session start so a
 * later stray emit cannot report a bogus age against a stale start.
 */
export function emitSessionEnd(
  reason: SessionEndReason,
  ctx: SessionEndContext = {}
): void {
  const route =
    ctx.route ??
    (typeof window !== 'undefined' ? window.location.pathname : null);
  const sessionAgeS =
    ctx.sessionAgeS ??
    (sessionStartMs != null
      ? Math.max(0, Math.round((Date.now() - sessionStartMs) / 1000))
      : null);
  const wasIdle = ctx.wasIdle ?? safeBool(isIdleGetter);
  const hadInflightRequest =
    ctx.hadInflightRequest ?? INFLIGHT_REASONS.has(reason);

  reportSessionEnd(
    { reason, route, sessionAgeS, wasIdle, hadInflightRequest },
    registeredCapture
  );

  clearSessionStart();
}
